import { useState } from 'react';
import { db } from '../db/client';
import { inviteMember } from '../db/auth';
import { avatarColor, avatarInitial, displayName } from './avatar';

export function MembersTab({ orgId, currentUserId }: { orgId: string; currentUserId: string }) {
  const { isLoading, data, error } = db.useQuery({
    organizations: {
      $: { where: { id: orgId } },
      owner: {},
      memberships: { user: {} },
    },
  });

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  if (isLoading) return <div className="uin-muted">Loading…</div>;
  if (error) return <div className="uin-error">{error.message}</div>;
  const org = data?.organizations?.[0];
  if (!org) return <div className="uin-muted">Organization not found.</div>;

  const isOwner = org.owner?.id === currentUserId;
  const memberships = org.memberships ?? [];

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    const emails = Array.from(
      new Set(
        email
          .split(/[,\s;]+/)
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
      ),
    );
    if (emails.length === 0) return;

    const valid: string[] = [];
    const invalid: string[] = [];
    for (const e2 of emails) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e2)) valid.push(e2);
      else invalid.push(e2);
    }

    setBusy(true);
    setErr(null);
    setOk(null);

    const failed: { email: string; reason: string }[] = invalid.map((e2) => ({
      email: e2,
      reason: 'invalid email',
    }));
    const invited: string[] = [];

    for (const e2 of valid) {
      try {
        await inviteMember(orgId, e2);
        invited.push(e2);
      } catch (err2) {
        failed.push({ email: e2, reason: (err2 as Error).message });
      }
    }

    const parts: string[] = [];
    if (invited.length === 1) {
      parts.push(`Invited ${invited[0]}.`);
    } else if (invited.length > 1) {
      parts.push(`Invited ${invited.length} (${invited.join(', ')}).`);
    }
    if (failed.length > 0) {
      const summary = failed.map((f) => `${f.email} — ${f.reason}`).join('; ');
      parts.push(`Failed: ${summary}`);
    }
    const message = parts.join(' ');

    if (invited.length > 0) {
      setOk(message);
      setEmail(failed.map((f) => f.email).join(', '));
    } else {
      setErr(message || 'No valid emails provided.');
    }
    setBusy(false);
  }

  async function handleRemove(membershipId: string) {
    if (!confirm('Remove this member from the organization?')) return;
    try {
      await db.transact([db.tx.memberships[membershipId].delete()]);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      {isOwner && (
        <>
          <div className="uin-section-title">Invite</div>
          {err && <div className="uin-error">{err}</div>}
          {ok && <div className="uin-muted" style={{ marginBottom: 8 }}>{ok}</div>}
          <form onSubmit={handleInvite} style={{ marginBottom: 12 }}>
            <input
              type="email"
              multiple
              className="uin-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com, another@example.com"
            />
            <button className="uin-btn-primary" type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Inviting…' : 'Send invites'}
            </button>
          </form>
        </>
      )}

      <div className="uin-section-title">Members ({memberships.length})</div>
      {memberships.map((m) => {
        const email = m.user?.email ?? m.email;
        const c = avatarColor(email);
        return (
          <div key={m.id} className="uin-member-row">
            <div className="uin-member-avatar" style={{ background: c.bg, color: c.fg }}>
              {avatarInitial(email)}
            </div>
            <div className="uin-member-meta">
              <div className="uin-member-name">{displayName(email)}</div>
              <div className="uin-member-email">{email}</div>
              <div style={{ marginTop: 4 }}>
                <span className={`uin-badge ${m.role === 'owner' ? 'owner' : 'member'}`}>{m.role}</span>{' '}
                <span className={`uin-badge ${m.status === 'invited' ? 'invited' : 'active'}`}>{m.status}</span>
              </div>
            </div>
            {isOwner && m.role !== 'owner' && (
              <button className="uin-btn-danger" onClick={() => handleRemove(m.id)}>
                Remove
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
